import { useQuery } from '@tanstack/react-query';
import { Divider, Dropdown, Input, MenuProps, Table, TableProps, Tag } from 'antd';
import { MyModal } from 'components';
import debounce from 'lodash/debounce';
import { IKeys, IWork } from 'models';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMPLETED_ROUTE, MY_WORKS_ROUTE } from 'routes/consts';
import { workService } from 'services';
import { formatPhoneNumber } from 'utils';
import { getWorkNumber } from 'utils/get-work-number';

const PER_PAGE = 12;

export const Search = () => {
	const [page, setPage] = useState(1);
	const [work, setWork] = useState<IWork | null>(null);
	const [search, setSearch] = useState<string>();
	const navigate = useNavigate();
	const handleSearch = (value: string) => {
		setSearch(value);
	};
	const debounced = useMemo(() => debounce(handleSearch, 400), []);
	const { data, isLoading } = useQuery({
		queryFn: () => workService.search({ page, search }),
		queryKey: [IKeys.WORKS, { page, search }],
		enabled: !!search
	});

	const actionItems = (work: IWork): MenuProps['items'] => ([{
		label: 'Manager form',
		key: 'manager',
		onClick: () => {
			setWork(work);
		}
	},
	{
		label: 'Foreman form',
		key: 'foreman',
		onClick: () => {
			navigate(`${MY_WORKS_ROUTE}/${work.id}`);
		}
	},
	{
		label: 'Completed details',
		key: 'completed',
		onClick: () => {
			navigate(`${COMPLETED_ROUTE}/${work.id}`);
		}
	}
	]);
	const columns: TableProps<IWork>['columns'] = [
		{
			title: 'Work',
			dataIndex: 'id',
			key: 'id',
			render: (_, record) => <Dropdown 
				menu={{ items: actionItems(record) }} 
				trigger={['click']}
			><Tag color="blue">{getWorkNumber(record)}</Tag></Dropdown>
		}, {
			title: 'Name',
			dataIndex: ['acf', 'customer_info', 'customer_name'],
			key: 'name'
		}, {
			title: 'Phone',
			dataIndex: ['acf', 'customer_info', 'customer_phone'],
			key: 'phone',
			render: text => <div onClick={(e) => e.stopPropagation()} style={{ minWidth: 120 }}><a href={`tel:${text}`}>{formatPhoneNumber(text)}</a></div>
		}, {
			title: 'Email',
			dataIndex: ['acf', 'customer_info', 'customer_email'],
			key: 'email',
			render: text => <a onClick={(e) => e.stopPropagation()} href={`mailto:${text}`}>{text}</a>
		}];
	return (
		<>
			<Input.Search 
				placeholder="Input a customer data or work ID"
				allowClear
				size="large"
				onChange={(val) => debounced(val.target.value)}
				onSearch={(val) => setSearch(val)}
				enterButton="Search"
			/>
			<Divider/>
			<Table<IWork>
				loading={isLoading}
				size="small"
				rowKey={(record) => record.id}
				dataSource={data?.data}
				columns={columns}
				pagination={{
					total: data?.headers['x-wp-total'],
					current: page,
					pageSize: PER_PAGE,
					onChange: (val) => setPage(val)
				}}
			/>
			<MyModal
				work={work}
				handleCancel={() => setWork(null)}
				queryKey={[IKeys.WORKS, { page, search }]}
			/>
		</>
	);
};